import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface Message {
  subject: string;
  category: string;
  priority: string;
  message: string;
  department: string;
}

const ContactAdmin = () => {
  const routes = all_routes;
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [messageData, setMessageData] = useState<Message>({
    subject: "",
    category: "",
    priority: "normal",
    message: "",
    department: "administration",
  });

  const categories = [
    { value: "general", label: "General Inquiry", icon: "ti-help" },
    { value: "payroll", label: "Payroll & Benefits", icon: "ti-credit-card" },
    { value: "schedule", label: "Schedule & Timetable", icon: "ti-calendar" },
    {
      value: "facilities",
      label: "Facilities & Maintenance",
      icon: "ti-building",
    },
    { value: "curriculum", label: "Curriculum & Academic", icon: "ti-book" },
    { value: "policy", label: "Policy & Procedure", icon: "ti-file-text" },
    {
      value: "grievance",
      label: "Grievance & Complaint",
      icon: "ti-alert-triangle",
    },
    { value: "other", label: "Other", icon: "ti-dots" },
  ];

  const departments = [
    { value: "administration", label: "Administration Office" },
    { value: "principal", label: "Principal Office" },
    { value: "hr", label: "Human Resources" },
    { value: "finance", label: "Finance Department" },
    { value: "academic", label: "Academic Affairs" },
    { value: "it", label: "IT Support" },
  ];

  const priorities = [
    { value: "low", label: "Low Priority", color: "info" },
    { value: "normal", label: "Normal", color: "primary" },
    { value: "high", label: "High Priority", color: "warning" },
    { value: "urgent", label: "Urgent", color: "danger" },
  ];

  const handleInputChange = (field: keyof Message, value: string) => {
    setMessageData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!messageData.subject || !messageData.category || !messageData.message) {
      alert("Please fill in all required fields.");
      return;
    }

    console.log("Sending message:", messageData);
    setShowSuccessModal(true);

    // Reset form
    setTimeout(() => {
      setMessageData({
        subject: "",
        category: "",
        priority: "normal",
        message: "",
        department: "administration",
      });
    }, 2000);
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Contact Administration</h3>
              <p className="text-muted mb-0">
                Send messages to school administration
              </p>
            </div>
          </div>

          {/* Quick Contact Options */}
          <div className="row mb-4">
            <div className="col-md-4 col-sm-6 mb-3">
              <div className="card bg-primary-light h-100">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-primary mb-2">
                    <i className="ti ti-phone text-white fs-2"></i>
                  </div>
                  <h6>Emergency Contact</h6>
                  <p className="mb-2 small text-muted">For urgent matters</p>
                  <a href="tel:+1234567890" className="btn btn-primary btn-sm">
                    Call Now
                  </a>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 mb-3">
              <div className="card bg-success-light h-100">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-success mb-2">
                    <i className="ti ti-mail text-white fs-2"></i>
                  </div>
                  <h6>Direct Email</h6>
                  <p className="mb-2 small text-muted">admin@school.edu</p>
                  <a
                    href="mailto:admin@school.edu"
                    className="btn btn-success btn-sm"
                  >
                    Send Email
                  </a>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6 mb-3">
              <div className="card bg-info-light h-100">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-info mb-2">
                    <i className="ti ti-clock text-white fs-2"></i>
                  </div>
                  <h6>Office Hours</h6>
                  <p className="mb-2 small text-muted">Mon-Fri: 8AM-5PM</p>
                  <span className="badge bg-success">Open Now</span>
                </div>
              </div>
            </div>
          </div>

          {/* Message Form */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="ti ti-message me-2"></i>Send Message
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-4">
                  <div className="col-md-6">
                    <label className="form-label">
                      Department <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={messageData.department}
                      onChange={(e) =>
                        handleInputChange("department", e.target.value)
                      }
                      required
                    >
                      {departments.map((dept) => (
                        <option key={dept.value} value={dept.value}>
                          {dept.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Category <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={messageData.category}
                      onChange={(e) =>
                        handleInputChange("category", e.target.value)
                      }
                      required
                    >
                      <option value="">Select category...</option>
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Priority Level</label>
                    <select
                      className="form-select"
                      value={messageData.priority}
                      onChange={(e) =>
                        handleInputChange("priority", e.target.value)
                      }
                    >
                      {priorities.map((priority) => (
                        <option key={priority.value} value={priority.value}>
                          {priority.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Subject <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Brief subject of your message..."
                      value={messageData.subject}
                      onChange={(e) =>
                        handleInputChange("subject", e.target.value)
                      }
                      required
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">
                      Message <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={8}
                      placeholder="Please provide detailed information about your inquiry or concern..."
                      value={messageData.message}
                      onChange={(e) =>
                        handleInputChange("message", e.target.value)
                      }
                      required
                    ></textarea>
                  </div>
                  <div className="col-12">
                    <div className="d-flex gap-2">
                      <button type="submit" className="btn btn-primary">
                        <i className="ti ti-send me-2"></i>Send Message
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() =>
                          setMessageData({
                            subject: "",
                            category: "",
                            priority: "normal",
                            message: "",
                            department: "administration",
                          })
                        }
                      >
                        <i className="ti ti-refresh me-2"></i>Clear Form
                      </button>
                      <Link
                        to={routes.teacherDashboard}
                        className="btn btn-outline-secondary"
                      >
                        <i className="ti ti-arrow-left me-2"></i>Back
                      </Link>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Common Topics */}
          <div className="card mt-4">
            <div className="card-header">
              <h6 className="card-title mb-0">Common Topics</h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {categories.map((category) => (
                  <div key={category.value} className="col-lg-6">
                    <div
                      className="d-flex align-items-center p-2 bg-light rounded cursor-pointer hover-card"
                      onClick={() =>
                        handleInputChange("category", category.value)
                      }
                    >
                      <div className="avatar avatar-sm bg-primary-transparent me-3">
                        <i className={`ti ${category.icon} text-primary`}></i>
                      </div>
                      <div>
                        <h6 className="mb-0">{category.label}</h6>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Response Time Info */}
          <div className="card mt-4">
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-3 col-sm-6 mb-3">
                  <div className="text-success">
                    <i className="ti ti-check-circle fs-2"></i>
                    <h6 className="mt-2">Urgent Matters</h6>
                    <p className="mb-0 small text-muted">Within 2 hours</p>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                  <div className="text-warning">
                    <i className="ti ti-clock fs-2"></i>
                    <h6 className="mt-2">High Priority</h6>
                    <p className="mb-0 small text-muted">Within 24 hours</p>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                  <div className="text-primary">
                    <i className="ti ti-calendar fs-2"></i>
                    <h6 className="mt-2">Normal Requests</h6>
                    <p className="mb-0 small text-muted">2-3 business days</p>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                  <div className="text-info">
                    <i className="ti ti-message fs-2"></i>
                    <h6 className="mt-2">General Inquiries</h6>
                    <p className="mb-0 small text-muted">3-5 business days</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title text-success">
                  <i className="ti ti-check-circle me-2"></i>Message Sent
                  Successfully
                </h5>
              </div>
              <div className="modal-body text-center">
                <div className="avatar avatar-xl bg-success-transparent mb-3">
                  <i className="ti ti-mail-check fs-1 text-success"></i>
                </div>
                <h6>Your message has been sent to administration.</h6>
                <p className="text-muted mb-0">
                  You will receive a response based on the priority level
                  selected. A copy has been sent to your email.
                </p>
              </div>
              <div className="modal-footer border-0 justify-content-center">
                <button
                  className="btn btn-success"
                  onClick={() => setShowSuccessModal(false)}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactAdmin;
